import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Field, KeyValue, Section, Select } from '@zt/ui';
import { api } from '../lib/api';
import { HelpTip } from '../lib/help';
import { useT } from '../lib/i18n';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { CompanyDetail, SalesRegion, SalesUser } from '../lib/types';

/** Temsilci veya bölge sorumlusu olabilecek kullanıcılar (F7-08). */
export function useSalesUsers(enabled = true) {
  return useQuery({
    queryKey: ['sales-users'],
    queryFn: () => api.get<SalesUser[]>('/v1/admin/sales-users'),
    enabled,
  });
}

export function useSalesRegions(enabled = true) {
  return useQuery({
    queryKey: ['sales-regions'],
    queryFn: () => api.get<SalesRegion[]>('/v1/admin/sales-regions'),
    enabled,
  });
}

/**
 * Bayi ayrıntısında satış temsilcisi ve bölge (F7-08, ADR-111). Atamayı `b2b.sales.manage` yetkili ve bayi kapsamı
 * sınırlı olmayan kullanıcı değiştirir; diğerleri görür.
 */
export function DealerSalesSection({ company }: { company: CompanyDetail }) {
  const t = useT();
  const { can, dealerScoped } = usePermissions();
  const editable = can('b2b.sales.manage') && !dealerScoped;
  const users = useSalesUsers(editable);
  const regions = useSalesRegions(editable);
  const [rep, setRep] = useState(company.salesRepUserId ?? '');
  const [region, setRegion] = useState(company.salesRegionId ?? '');
  const save = useApiMutation(
    () =>
      api.put(`/v1/admin/companies/${company.id}/sales`, {
        salesRepUserId: rep || null,
        salesRegionId: region || null,
      }),
    { invalidate: [['company', company.id], ['companies'], ['sales-regions']], success: t('common.saved') },
  );
  const changed = (rep || null) !== company.salesRepUserId || (region || null) !== company.salesRegionId;
  return (
    <Section
      title={t('admin.sales.sectionTitle')}
      description={t('admin.sales.sectionLead')}
      action={<HelpTip k="dealers.sales_assignment" />}
    >
      {editable ? (
        <div className="flex flex-wrap items-end gap-3">
          <Field
            label={t('admin.sales.rep')}
            help={<HelpTip k="dealers.sales_rep" />}
            error={fieldError(save.error, 'salesRepUserId')}
          >
            {(p) => (
              <Select {...p} value={rep} onChange={(e) => setRep(e.target.value)}>
                <option value="">{t('admin.sales.noRep')}</option>
                {users.data?.map((u) => (
                  <option key={u.userId} value={u.userId}>
                    {u.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field
            label={t('admin.sales.region')}
            help={<HelpTip k="dealers.sales_region" />}
            error={fieldError(save.error, 'salesRegionId')}
          >
            {(p) => (
              <Select {...p} value={region} onChange={(e) => setRegion(e.target.value)}>
                <option value="">{t('admin.sales.noRegion')}</option>
                {regions.data?.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Button loading={save.isPending} disabled={!changed} onClick={() => save.mutate(undefined)}>
            {t('admin.sales.saveAssignment')}
          </Button>
        </div>
      ) : (
        <KeyValue
          items={[
            { label: t('admin.sales.rep'), value: company.salesRepName ?? t('admin.sales.noRep') },
            { label: t('admin.sales.region'), value: company.salesRegionName ?? t('admin.sales.noRegion') },
          ]}
        />
      )}
    </Section>
  );
}
